/**
 * System Settings Service
 * API client for system settings management
 */

import api from "@/shared/api.jsx";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * Get public settings (no auth required)
 * @returns {Promise<Object>} Public settings object
 */
export const getPublicSettings = async () => {
  try {
    const response = await api.get("/settings/public");
    logInfo("[SystemSettingsService] Fetched public settings successfully");
    return response;
  } catch (error) {
    logError("[SystemSettingsService] Failed to get public settings", {
      error: error.message,
    });
    throw error;
  }
};

/**
 * Get all settings (admin only)
 * @param {Object} options - Query options
 * @returns {Promise<Object>} All settings
 */
export const getAllSettings = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.category) {
      params.append("category", options.category);
    }
    const queryString = params.toString();
    const url = `/settings${queryString ? `?${queryString}` : ""}`;
    const response = await api.get(url);
    logInfo("[SystemSettingsService] Fetched all settings successfully", {
      category: options.category,
      count: response.data?.length || 0,
    });
    return response;
  } catch (error) {
    logError("[SystemSettingsService] Failed to get all settings", {
      options,
      error: error.message,
    });
    throw error;
  }
};

/**
 * Get a single setting by key
 * @param {string} key - Setting key
 * @returns {Promise<Object>} Setting data
 */
export const getSetting = async (key) => {
  try {
    const response = await api.get(`/settings/${encodeURIComponent(key)}`);
    logInfo("[SystemSettingsService] Fetched setting successfully", { key });
    return response;
  } catch (error) {
    logError("[SystemSettingsService] Failed to get setting", {
      key,
      error: error.message,
    });
    throw error;
  }
};

/**
 * Set a single setting
 * @param {string} key - Setting key
 * @param {*} value - Setting value
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Updated setting
 */
export const setSetting = async (key, value, options = {}) => {
  try {
    const response = await api.put("/settings", {
      key,
      value,
      ...options,
    });
    logInfo("[SystemSettingsService] Updated setting successfully", { key });
    return response;
  } catch (error) {
    logError("[SystemSettingsService] Failed to set setting", {
      key,
      value,
      options,
      error: error.message,
    });
    throw error;
  }
};

/**
 * Set multiple settings at once
 * @param {Array<Object>} settings - Array of {key, value, ...options}
 * @returns {Promise<Object>} Updated settings
 */
export const setMultipleSettings = async (settings) => {
  try {
    const response = await api.post("/settings/bulk", {
      settings,
    });
    logInfo("[SystemSettingsService] Updated multiple settings successfully", {
      count: settings.length,
    });
    return response;
  } catch (error) {
    logError("[SystemSettingsService] Failed to set multiple settings", {
      settingsCount: settings.length,
      error: error.message,
    });
    throw error;
  }
};
